import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../../context/AnalyticsContext'
import { useAuth } from '../../context/AuthContext'
import Modal from '../../components/Modal'
import LegalDocContent, { getLegalDocTitle } from '../../components/LegalDocContent'
import { getOnboarding, setOnboarding } from './onboardingStorage'

const TERMS_PDF_URL = '/legal/terms-and-conditions.pdf'
import './onboarding.css'

const OnboardingGender = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, loading, signIn, resetPassword, checkEmailExists } = useAuth()
  const [showCookie, setShowCookie] = useState(false)
  const [legalDoc, setLegalDoc] = useState(null)
  const [showLogin, setShowLogin] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotError, setForgotError] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState(false)
  const [forgotLoading, setForgotLoading] = useState(false)

  const consent = useMemo(() => getOnboarding().cookiesConsent || 'unknown', [])

  useEffect(() => {
    // Сессия известна сразу после getSession; loading ещё true пока грузится профиль — не ждём, иначе залогиненный видит главную.
    if (user) {
      navigate('/app', { replace: true })
      return
    }
    if (!loading && consent === 'unknown') {
      setShowCookie(true)
    }
  }, [loading, user, navigate, consent])

  // Проверка email при вводе (только email, без пароля)
  const onLoginEmailSubmit = async (e) => {
    e.preventDefault()
    setLoginError('')
    const email = loginEmail.trim().toLowerCase()
    
    if (!email) {
      setLoginError(t('onboarding.loginEmailRequired'))
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setLoginError(t('onboarding.loginEmailInvalid'))
      return
    }

    setCheckingEmail(true)
    try {
      // Проверяем существует ли пользователь с таким email
      const { exists } = await checkEmailExists(email)
      
      if (exists) {
        // Пользователь существует - сохраняем email и переходим на ввод пароля
        setOnboarding({ email })
        navigate('/onboarding/password/existing', { replace: true })
      } else {
        // Пользователя нет - показываем ошибку
        setLoginError(t('onboarding.loginEmailNotFound'))
      }
    } catch (err) {
      console.error('Error checking email:', err)
      setLoginError(t('onboarding.loginEmailError'))
    } finally {
      setCheckingEmail(false)
    }
  }

  // Старый метод для ввода пароля (используется на странице password/existing)
  const onLoginSubmit = async (e) => {
    e.preventDefault()
    setLoginError('')
    const email = loginEmail.trim().toLowerCase()
    const password = loginPassword
    if (!email || !password) {
      setLoginError(t('onboarding.loginEmailRequired'))
      return
    }
    setLoginLoading(true)
    try {
      const { user: u, error } = await signIn(email, password)
      if (error) {
        setLoginError(error || 'Invalid email or password. If you don\'t have an account, please sign up first.')
        setLoginLoading(false)
        return
      }
      if (u) {
        navigate('/app', { replace: true })
      }
    } catch (err) {
      setLoginError('Something went wrong. Please try again.')
      setLoginLoading(false)
    }
  }

  const pickGender = (gender) => {
    setOnboarding({ gender })
    trackEvent('funnel_gender_completed', { step: 1, gender })
    navigate('/onboarding/name')
  }

  const acceptAll = () => {
    setOnboarding({ cookiesConsent: 'all' })
    trackEvent('cookie_consent', { decision: 'accept_all' })
    setShowCookie(false)
  }

  const declineNonEssential = () => {
    setOnboarding({ cookiesConsent: 'decline_non_essential' })
    trackEvent('cookie_consent', { decision: 'decline_non_essential', cookieless: true })
    setShowCookie(false)
  }

  if (showLogin) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48, paddingBottom: 120 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button
              type="button"
              className="link"
              onClick={() => { setShowLogin(false); setLoginError(''); setLoginEmail(''); setLoginPassword(''); }}
              style={{ fontSize: 16 }}
            >
              ← {t('common.back')}
            </button>
          </div>
          <h1 className="h1">{t('onboarding.loginTitle')}</h1>
          <p className="p">{t('onboarding.loginSubtitle')}</p>
          <form onSubmit={onLoginEmailSubmit} style={{ marginTop: 20 }}>
            <div className="field">
              <label className="label">{t('onboarding.loginEmailLabel')}</label>
              <input
                className={`input ${loginError ? 'error' : ''}`}
                type="email"
                value={loginEmail}
                onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                placeholder={t('onboarding.loginEmailPlaceholder')}
                inputMode="email"
                autoComplete="email"
                autoFocus
              />
            </div>
            {loginError ? <div className="error-text" style={{ marginTop: 8, marginBottom: 12 }}>{loginError}</div> : null}
          </form>
          <button
            type="submit"
            className="cta-fixed"
            disabled={checkingEmail}
            onClick={(e) => {
              e.preventDefault()
              onLoginEmailSubmit(e)
            }}
          >
            {checkingEmail ? t('common.checking') : t('common.continue')}
          </button>
        </div>
      </div>
    )
  }

  const onForgotPasswordSubmit = async (e) => {
    e.preventDefault()
    setForgotError('')
    const email = forgotEmail.trim().toLowerCase()
    
    if (!email) {
      setForgotError(t('onboarding.loginEmailRequired'))
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setForgotError(t('onboarding.loginEmailInvalid'))
      return
    }

    setForgotLoading(true)
    try {
      const { error } = await resetPassword(email)
      if (error) {
        setForgotError(error || t('onboarding.forgotPasswordError'))
        setForgotLoading(false)
        return
      }
      setForgotSuccess(true)
      setForgotLoading(false)
    } catch (err) {
      setForgotError(t('onboarding.forgotPasswordError'))
      setForgotLoading(false)
    }
  }

  if (showForgotPassword) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content" style={{ paddingTop: 48 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <button
              type="button"
              className="link"
              onClick={() => {
                setShowForgotPassword(false)
                setForgotError('')
                setForgotEmail('')
                setForgotSuccess(false)
              }}
              style={{ fontSize: 16 }}
            >
              ← {t('common.back')}
            </button>
          </div>
          
          {forgotSuccess ? (
            <>
              <h1 className="h1">{t('onboarding.forgotPasswordCheckEmail')}</h1>
              <p className="p">
                {t('onboarding.forgotPasswordSent', { email: forgotEmail })}
              </p>
            </>
          ) : (
            <>
              <h1 className="h1">{t('onboarding.forgotPasswordTitle')}</h1>
              <p className="p">
                {t('onboarding.forgotPasswordSubtitle')}
              </p>
              <form onSubmit={onForgotPasswordSubmit} style={{ marginTop: 20 }}>
                <div className="field">
                  <label className="label">{t('onboarding.loginEmailLabel')}</label>
                  <input
                    className={`input ${forgotError ? 'error' : ''}`}
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value)
                      setForgotError('')
                    }}
                    placeholder={t('onboarding.loginEmailPlaceholder')}
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                {forgotError ? <div className="error-text" style={{ marginBottom: 12 }}>{forgotError}</div> : null}
                <button type="submit" className="cta-fixed" disabled={forgotLoading} style={{ position: 'relative', marginTop: 8 }}>
                  {forgotLoading ? t('common.sending') : t('onboarding.forgotPasswordSendLink')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="onboarding-page">
      <button
        type="button"
        className="link"
        onClick={() => navigate('/login')}
        style={{ position: 'absolute', top: 16, right: 16, zIndex: 10, fontSize: 16 }}
      >
        {t('onboarding.loginTitle')}
      </button>
      <div className="onboarding-content">
        <img className="square-hero" src="/Main.png" alt="AstroMatch" />

        <h1 className="h1" style={{ textAlign: 'center', fontSize: '24px' }}>{t('onboarding.genderTitle')}</h1>
        <p className="p" style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
          {t('onboarding.genderSubtitle')}
        </p>

        <div className="chips one">
          <button className="chip" onClick={() => pickGender('male')}>
            {t('onboarding.genderMan')}
          </button>
          <button className="chip" onClick={() => pickGender('female')}>
            {t('onboarding.genderWoman')}
          </button>
        </div>

        <div className="legal-links">
          <button className="link" onClick={() => setLegalDoc('terms-and-conditions')}>
            {t('paywall.termsOfUse')}
          </button>
          <button className="link" onClick={() => setLegalDoc('privacy-policy')}>
            {t('paywall.privacyPolicy')}
          </button>
        </div>
      </div>

      {showCookie ? (
        <Modal title={t('onboarding.cookiesTitle')} onClose={() => setShowCookie(false)}>
          <p className="p" style={{ whiteSpace: 'pre-line' }}>
            {t('onboarding.cookiesText')}
          </p>
          <div className="modal-actions">
            <button className="primary" onClick={acceptAll}>
              {t('onboarding.cookiesAcceptAll')}
            </button>
            <button className="secondary" onClick={declineNonEssential}>
              {t('onboarding.cookiesDecline')}
            </button>
          </div>
        </Modal>
      ) : null}

      {legalDoc ? (
        <Modal title={getLegalDocTitle(legalDoc)} onClose={() => setLegalDoc(null)}>
          {legalDoc === 'terms-and-conditions' ? (
            <div style={{ maxHeight: '70vh', overflow: 'hidden', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}>
              <iframe
                src={TERMS_PDF_URL}
                title={getLegalDocTitle(legalDoc)}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
              <p className="p" style={{ marginTop: 12 }}>
                If the document doesn&apos;t load,{' '}
                <a href={TERMS_PDF_URL} target="_blank" rel="noreferrer">
                  open it in a new tab
                </a>
                .
              </p>
            </div>
          ) : (
            <LegalDocContent docId={legalDoc} />
          )}
        </Modal>
      ) : null}
    </div>
  )
}

export default OnboardingGender

